import type { RegradeView } from "@/lib/api/regrades";
import { HELD, SAID, exactly, outcomeWords, questionLabel } from "./regrade-words";

/** Reasons for changed questions, each named by its number and prompt. */
export function QuestionReasons({ view, reasons }: { view: Pick<RegradeView, "questions">; reasons: Record<string, string> }) {
  const entries = Object.entries(reasons);
  if (!entries.length) return null;
  return <dl className="grid gap-1" aria-label="Reasons for changed questions">{entries.map(([key, reason]) =>
    <div key={key}><dt dir="auto" className="text-meta font-semibold">{questionLabel(view, key)}</dt><dd dir="auto" className="whitespace-pre-wrap">{reason}</dd></div>)}</dl>;
}

/** The released decision as its learner reads it: reasons, the previous result and the new one. */
export function ReleasedOutcome({ view, timezone }: { view: RegradeView; timezone: string }) {
  const outcome = view.outcome;
  if (!outcome) return null;
  return <div data-regrade-outcome={outcome.outcome} className="grid gap-1.5">
    <p className="text-ui font-medium">{outcomeWords(outcome.outcome, outcome.previous, outcome.result, view.points)}</p>
    <p className="text-meta text-muted-foreground"><time dateTime={outcome.released_at}>Decision released {exactly(outcome.released_at, timezone)}</time></p>
    <blockquote dir="auto" className={SAID}>{outcome.reason}</blockquote>
    <QuestionReasons view={view} reasons={outcome.question_reasons} />
  </div>;
}

/** The learner's request as received, then pending, held or the released decision: never private marking progress. */
export function RequestRecord({ view, timezone }: { view: RegradeView; timezone: string }) {
  const deadline = view.deadline_at ? exactly(view.deadline_at, timezone) : null;
  return <div data-regrade={view.state} className="grid gap-1.5">
    {!view.request && view.state === "closed" && <p className="text-meta text-muted-foreground">The regrade window for this result closed {deadline}.</p>}
    {view.request && <>
      <p className="text-meta text-muted-foreground"><time dateTime={view.request.created_at}>Requested {exactly(view.request.created_at, timezone)}</time></p>
      <blockquote dir="auto" className={SAID}>{view.request.reason}</blockquote>
      {view.held ? <p role="status" data-regrade-status="held" className="text-ui">{HELD}</p>
        : view.request.status === "pending" ? <p role="status" data-regrade-status="pending" className="text-ui">Received. Your instructor will release a reasoned decision; until then your released result stands.</p>
        : <ReleasedOutcome view={view} timezone={timezone} />}
    </>}
  </div>;
}
